import React, {ReactElement, useEffect, useMemo, useState} from "react";
import moment from "moment";
import * as styles from "./styles/PlacementLevelChangeModal.module.css";
import {
	getMixpanelDataFromUser,
	getPlacementLevelAndChapter,
	takePlacementTestFromOptIn,
} from "src/core/home/util/PageOnboardingUtil";
import {User} from "shared/types/VaultTypes";
import withVaultDispatch from "src/core/hoc/withVaultDispatch";
import ChooseOptionModal from "src/core/view/ChooseOptionModal";
import AssetsUtil from "src/core/util/AssetsUtil";
import {useUserPlacementTestInfo} from "../util/useUserPlacementTestInfo";
import OpsUser from "../vault/OpsUser";
import {AT} from "../vault/ActionTypeConstants";
import ColorUtil from "../util/ColorUtil";
import {track} from "../util/Mixpanel";
import URLMake from "shared/URLMake";
import {titleCaseName} from "src/parent/primaryRegistration/studentNameDisplay";

const chapterNames = require("../../../shared/data/dbGradeChapterBlock.json")
	.chapterNames;

type ModalStateOpen = {
	isInitialized: true;
	isOpen: true;
	needsToTakePlacementTest: boolean;
};
type ModalStateClosed = {
	isInitialized: boolean;
	isOpen: false;
};
type ModalState = ModalStateOpen | ModalStateClosed;

export const PlacementLevelChangedModal = withVaultDispatch(
	({user, dispatch}: {user: User; dispatch: (action: unknown) => void}) => {
		const [modalState, setModalState] = useState<ModalState>({
			isInitialized: false,
			isOpen: false,
		});
		const placementTest = useUserPlacementTestInfo({user, dispatch});
		const {
			level: gradeID,
			chapterIndex: placementChapterIndex,
		} = getPlacementLevelAndChapter(placementTest, user);
		const studentProfilePlacementTest =
			user.model?.eventsTriggered.notifications.studentProfilePlacementTest;
		const completedLastPlacementTestAt =
			placementTest?.previousResults?.results?.[0]?.completedAt;

		// Try to initialize modalState...
		useEffect(() => {
			// Once the placementTest info has loaded, let's only initialize the modalState once...
			if (modalState.isInitialized) return;
			if (!user.model || !placementTest) {
				return;
			}

			// Wrap this check in a function that returns true or false because:
			// if the user model and placement test info are both loaded, we always want to
			// initialize the modal as either open or closed, and don't change that during normal play.
			const shouldOpenModal = () => {
				// We don't want to notify user of placement test level change if they're already in the normal student onboarding flow.
				if (OpsUser.shouldSeeStudentOnboarding(user)) return false;

				// If user hasn't recently triggered a placement test via the student profile,
				// or we can't load placement test info, don't show modal.
				if (!placementTest) return false;
				if (
					!studentProfilePlacementTest ||
					studentProfilePlacementTest === "dismissed"
				)
					return false;
				// Only want to notify user if they finished a placement test since starting the last one through the student profile.
				// or if the student was flagged as "needs-to-take" as a result of placement test load error during onboarding.
				if (
					studentProfilePlacementTest === "needs-to-take" ||
					(completedLastPlacementTestAt &&
						moment(studentProfilePlacementTest) <=
							moment(completedLastPlacementTestAt))
				) {
					return true;
				}

				return false;
			};

			if (shouldOpenModal()) {
				setModalState({
					isInitialized: true,
					isOpen: true,
					needsToTakePlacementTest:
						studentProfilePlacementTest === "needs-to-take",
				});
			} else {
				setModalState(prev => ({
					...prev,
					isInitialized: true,
					isOpen: false,
				}));
			}
		}, [
			dispatch,
			user,
			placementTest,
			modalState,
			completedLastPlacementTestAt,
			studentProfilePlacementTest,
		]);

		const didPlacementTestChangeLevel = useMemo(
			() =>
				modalState.isOpen &&
				(gradeID !== user.model?.gradeID.toString() ||
					placementChapterIndex !== user.model?.placementChapterIndex),
			[gradeID, modalState.isOpen, placementChapterIndex, user]
		);

		const closeModal = () => setModalState(prev => ({...prev, isOpen: false}));

		const onCancel = () => {
			if (!modalState.isOpen) return;
			track("Placement Test Modal: Click Cancel", {
				...getMixpanelDataFromUser(user),
			});
			closeModal();
			dispatch({
				type: AT.UPDATE_USER_EVENTS_TRIGGERED,
				pathArray: ["notifications", "studentProfilePlacementTest"],
				propValue: "dismissed",
			});
		};

		const renderModal = (buttons: Object[], dialog: ReactElement) => (
			<ChooseOptionModal
				cannotClose
				modalClass={styles.placementTestModal}
				styleType="placementTest"
				levelType="aboveAll"
				buttonsContainerClass={styles.buttons}
				buttons={buttons}
			>
				<img
					className={styles.modalHeadmaster}
					src={AssetsUtil.getTestOverviewBeast(
						"headmaster",
						gradeID ?? user.model!.gradeID
					)}
				/>
				{dialog}
			</ChooseOptionModal>
		);

		if (!modalState.isOpen) return null;
		if (modalState.needsToTakePlacementTest) {
			const modalContent = usePlacementTestPromptModalContent(
				user,
				dispatch,
				onCancel,
				closeModal
			);
			return renderModal(modalContent.buttons, modalContent.dialog);
		} else if (didPlacementTestChangeLevel) {
			const modalContent = usePlacementTestLevelChangedModalContent(
				user,
				dispatch,
				onCancel,
				gradeID,
				placementChapterIndex
			);
			return renderModal(modalContent.buttons, modalContent.dialog);
		} else {
			const modalContent = usePlacementTestLevelStayedTheSameModalContent(
				user,
				dispatch,
				onCancel
			);
			return renderModal(modalContent.buttons, modalContent.dialog);
		}
	}
);

const usePlacementTestPromptModalContent = (
	user: User,
	dispatch: (action: unknown) => void,
	onCancel: () => void,
	closeModal: () => void
) => {
	return {
		buttons: [
			{
				buttonText: "No, Keep My Level",
				variant: "outline",
				buttonType: "trapNormal",
				onLinkClick: onCancel,
				linkTo: URLMake.forChapterFromNumbers(
					user.model!.gradeID,
					user.model!.placementChapterIndex
				),
			},
			{
				buttonText: "Yes, Take Test",
				buttonType: "trapFlipped",
				handleClick: () => {
					closeModal();
					takePlacementTestFromOptIn(user, dispatch);
				},
			},
		],
		dialog: (
			<>
				<div className={styles.modalTitle}>
					Good news {titleCaseName(user.model!.displayName)}!
				</div>
				<div className={styles.modalDescription}>
					The placement test is available now. Ready to take it?
				</div>
			</>
		),
	};
};

const usePlacementTestLevelChangedModalContent = (
	user: User,
	dispatch: (action: unknown) => void,
	onCancel: () => void,
	gradeID: string,
	placementChapterIndex: number
) => {
	const onConfirm = () => {
		track("Placement Test Modal: Click Complete", {
			...getMixpanelDataFromUser(user),
		});
		dispatch({
			type: AT.UPDATE_USER_EVENTS_TRIGGERED,
			pathArray: ["notifications", "studentProfilePlacementTest"],
			propValue: "dismissed",
		});
		dispatch({
			type: AT.STUDENT_ONBOARDING_PLACEMENT_COMPLETE_REQUEST,
			payload: {
				grade: gradeID,
				chapterIndex: placementChapterIndex,
			},
		});
	};

	return {
		buttons: [
			{
				buttonText: "No, Keep My Level",
				variant: "outline",
				buttonType: "trapNormal",
				onLinkClick: onCancel,
				linkTo: URLMake.forChapterFromNumbers(
					user.model!.gradeID,
					user.model!.placementChapterIndex
				),
			},
			{
				buttonText: "Yes, Let's Go!",
				buttonType: "trapFlipped",
				handleClick: onConfirm,
			},
		],
		dialog: (
			<>
				<div className={styles.modalTitle}>Your level has changed!</div>
				<div className={styles.modalDescription}>
					You started in{" "}
					{`Level ${user.model!.gradeID}: ${
						chapterNames[parseInt(user.model!.gradeID.toString())][
							user.model!.placementChapterIndex
						]
					}`}
					, and now you're in{" "}
					<span style={{color: ColorUtil.getCurrentScheme().blue}}>
						{`Level ${gradeID}: ${
							chapterNames[parseInt(gradeID)][placementChapterIndex]
						}`}
						.
					</span>
				</div>
				<div className={styles.modalDescription}>
					Ready to start your adventure?
				</div>
			</>
		),
	};
};

const usePlacementTestLevelStayedTheSameModalContent = (
	user: User,
	dispatch: (action: unknown) => void,
	onCancel: () => void
) => {
	return {
		buttons: [
			{
				buttonText: "Let's Go!",
				buttonType: "trapNormal",
				onLinkClick: onCancel,
				linkTo: URLMake.forChapterFromNumbers(
					user.model!.gradeID,
					user.model!.placementChapterIndex
				),
			},
		],
		dialog: (
			<>
				<div className={styles.modalTitle}>You're right where you belong!</div>
				<div className={styles.modalDescription}>
					Great job! The test confirmed you're in the perfect spot in{" "}
					<span style={{color: ColorUtil.getCurrentScheme().blue}}>
						{`Level ${user.model!.gradeID}: ${
							chapterNames[parseInt(user.model!.gradeID.toString())][
								user.model!.placementChapterIndex
							]
						}`}
					</span>
					. Let's keep the adventure going!
				</div>
			</>
		),
	};
};
